import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const TicketSubmissionModal = ({ onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketRef, setTicketRef] = useState('');

  const categoryOptions = [
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing',   label: 'Billing Question' },
    { value: 'feature',   label: 'Feature Request' },
    { value: 'account',   label: 'Account Management' },
    { value: 'other',     label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low',      label: 'Low' },
    { value: 'medium',   label: 'Medium' },
    { value: 'high',     label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.subject || !formData.category || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id:    user?.id || null,
          user_email: user?.email || null,
          subject:    formData.subject.trim(),
          category:   formData.category,
          priority:   formData.priority,
          description: formData.description.trim(),
          status: 'open',
        })
        .select('ticket_ref')
        .single();

      if (error) throw error;
      setTicketRef(data?.ticket_ref || '');

      // Notify support inbox; surface failure so the user knows to follow up by email
      const { error: notifyErr } = await supabase.functions.invoke('notify-support-ticket', {
        body: {
          ticket_ref:  data?.ticket_ref,
          subject:     formData.subject.trim(),
          category:    formData.category,
          priority:    formData.priority,
          description: formData.description.trim(),
        },
      });
      if (notifyErr) {
        console.warn('[ticket] Support notification failed:', notifyErr.message);
        toast('Ticket saved. Email notification to support could not be sent — we will still review your ticket.', { icon: '⚠️', duration: 6000 });
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Ticket submission error:', err);
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Mail" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground">Submit Support Ticket</h3>
                <p className="text-sm text-muted-foreground">We'll get back to you within one business day</p>
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-smooth">
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {submitted ? (
          /* Success state */
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Ticket Submitted</h3>
            {ticketRef && (
              <p className="text-sm font-mono text-muted-foreground mb-3">
                Reference: <span className="text-foreground font-semibold">{ticketRef}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground mb-6">
              Our support team will review your ticket and respond within one business day. Check <strong>Help &amp; Support → My Tickets</strong> for updates.
            </p>
            <Button variant="default" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <Input
              label="Subject"
              type="text"
              required
              placeholder="Brief description of your issue"
              value={formData.subject}
              onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category <span className="text-destructive">*</span></label>
                <Select options={categoryOptions} value={formData.category} onChange={(v) => setFormData(p => ({ ...p, category: v }))} placeholder="Select category" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                <Select options={priorityOptions} value={formData.priority} onChange={(v) => setFormData(p => ({ ...p, priority: v }))} placeholder="Select priority" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Description <span className="text-destructive">*</span></label>
              <textarea
                required
                rows={6}
                placeholder="Please provide as much detail as possible about your issue..."
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Response Time</p>
                  <p>Our support team typically responds within one business day. Check <strong>My Tickets</strong> in the Support section for status updates.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
              <Button type="submit" variant="default" iconName="Send" iconPosition="left" loading={isSubmitting} disabled={isSubmitting}>Submit Ticket</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TicketSubmissionModal;
